import TableActions from '../../../../components/table/Actions';
import EstabelecimentosListStatus from './Status';
import {
    SALONFLOW_PLAN_LABEL,
    normalizePlanKey,
} from '../../../../plans/salonflowPlans';
import type { SalonFlowPlanKey } from '../../../../plans/salonflowPlans';

const formatDate = (value?: string | Date) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
};

const getTrialRemainingDays = (trialEndsAt?: string | Date) => {
    if (!trialEndsAt) return null;
    const end = new Date(trialEndsAt);
    if (Number.isNaN(end.getTime())) return null;
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const BillingStatusCell = ({ row }: { row: any }) => {
    if (row?.billingExempt) {
        return (
            <div className="space-y-1">
                <span className="badge badge-success">Contrato externo</span>
                <div className="text-xs text-base-content/70">Sem cobrança SalonFlow</div>
            </div>
        );
    }

    const status = row?.billingStatus || 'trial';
    const remainingDays = getTrialRemainingDays(row?.trialEndsAt);

    if (status === 'active') {
        return <span className="badge badge-success">Pagamento ativo</span>;
    }

    if (status === 'blocked') {
        const trialExpired = remainingDays !== null ? remainingDays <= 0 : false;
        return (
            <div className="space-y-1">
                <span className="badge badge-error">Bloqueado</span>
                <div className="text-xs text-base-content/70">
                    {trialExpired ? `Teste expirou em ${formatDate(row?.trialEndsAt)}` : 'Pagamento desativado'}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <span className="badge badge-warning">Trial</span>
            <div className="text-xs text-base-content/70">
                {remainingDays !== null ? `${Math.max(remainingDays, 0)} dia(s) restantes` : 'Em andamento'}
            </div>
        </div>
    );
};

const PLAN_BADGE_CLASS: Record<SalonFlowPlanKey, string> = {
    trial: 'badge-warning',
    basic: 'badge-info',
    professional: 'badge-primary',
    premium: 'badge-secondary',
};

const SystemPlanCell = ({ row }: { row: any }) => {
    if (row?.billingExempt) {
        return (
            <div className="space-y-1">
                <span className="badge badge-secondary">Isento</span>
                <div className="text-xs text-base-content/60">Acesso total</div>
            </div>
        );
    }

    const planKey = normalizePlanKey(row?.systemPlanKey);

    return (
        <div className="space-y-1">
            <span className={`badge ${PLAN_BADGE_CLASS[planKey]}`}>
                {SALONFLOW_PLAN_LABEL[planKey]}
            </span>
            {!row?.systemPlanKey && (
                <div className="text-xs text-base-content/60">Fallback aplicado</div>
            )}
        </div>
    );
};

export const getColumns = ({
    onActivateBilling,
    onDeactivateBilling,
    onChangeSystemPlan,
    onDelete,
    loadingActionId,
    deletingActionId,
}: {
    onActivateBilling: (row: any) => void;
    onDeactivateBilling: (row: any) => void;
    onChangeSystemPlan: (row: any) => void;
    onDelete: (row: any) => void;
    loadingActionId?: string | null;
    deletingActionId?: string | null;
}) => [
    {
        name: 'Situação',
        cell: (row: any) => <EstabelecimentosListStatus status={row.status} id={row.id} />,
    },
    {
        name: 'Nome',
        selector: (row: any) => row.name,
        sortable: true,
        cell: (row: any) => <div style={{ textAlign: 'right' }}>{row.name}</div>,
    },
    {
        name: 'Cobranca',
        cell: (row: any) => <BillingStatusCell row={row} />,
    },
    {
        name: 'Plano',
        cell: (row: any) => <SystemPlanCell row={row} />,
    },
    {
        name: 'Trial ate',
        selector: (row: any) => formatDate(row.trialEndsAt),
        cell: (row: any) => <span>{formatDate(row.trialEndsAt)}</span>,
    },
    {
        name: 'Ações',
        right: true,
        sortable: false,
        cell: (row: any) => {
            const options: any[] = [
                {
                    type: 'edit',
                },
            ];
            const billingAction =
                row?.billingStatus === 'active'
                    ? {
                          type: 'Desativar cobranca',
                          onClick: onDeactivateBilling,
                          disabled: loadingActionId === row?._id,
                      }
                    : {
                          type: 'Ativar cobranca',
                          onClick: onActivateBilling,
                          disabled: loadingActionId === row?._id,
                      };

            if (!row?.billingExempt) {
                options.push(
                    {
                        type: 'Alterar plano',
                        onClick: onChangeSystemPlan,
                        disabled: loadingActionId === row?._id,
                    },
                    billingAction,
                );
            }

            options.push({
                type: 'delete',
                onClick: onDelete,
                disabled: deletingActionId === row?._id,
            });

            return (
                <TableActions
                    row={row}
                    options={options}
                />
            );
        },
    },
];
